import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { DatePicker, Icon } from '@livable/saas-react-components';
import { Button, InputBase } from '@mui/material';
import moment from 'moment';
import React from 'react';
import { formatUtilityLabel } from 'src/pages/NewBillingModule/ApplyBillingFull/BillAutomationExtraction';
import useStyles from './billDetailsStyles';
import { formatMoney, getServiceGlCode, getServiceTypes, pickValue } from './billDetailsUtils';
import { removeProperty, removeServiceType, setGlCodeForServiceType, updateBillField, updateTotalDue } from './billEditUtils';
const DATE_KEYS = {
  billDate: ['billDate', 'postingDate'],
  dueDate: ['dueDate', 'billDueDate']
};
function toDate(value) {
  const parsed = moment(value);
  return value && parsed.isValid() ? parsed.toDate() : null;
}
function fromDate(date) {
  return date ? moment(date).format(moment.HTML5_FMT.DATE) : '';
}
function EditRow(_ref) {
  let children = _ref.children,
    deleteLabel = _ref.deleteLabel,
    label = _ref.label,
    onDelete = _ref.onDelete;
  const classes = useStyles();
  return /*#__PURE__*/React.createElement("div", {
    className: classes.editRow
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.editCellLabel
  }, label), /*#__PURE__*/React.createElement("div", {
    className: classes.editCellValue
  }, children), /*#__PURE__*/React.createElement("div", {
    className: classes.editCellAction
  }, /*#__PURE__*/React.createElement("button", {
    "aria-label": deleteLabel || "Clear ".concat(label),
    className: classes.editIconButton,
    onClick: onDelete,
    title: deleteLabel || "Clear ".concat(label),
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "delete"
  }))));
}
function ChipList(_ref2) {
  let items = _ref2.items,
    onRemove = _ref2.onRemove,
    removeLabel = _ref2.removeLabel;
  const classes = useStyles();
  return /*#__PURE__*/React.createElement("div", {
    className: classes.editChips
  }, items.map(item => /*#__PURE__*/React.createElement("span", {
    className: classes.editChip,
    key: item.key,
    title: item.label
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.editChipLabel
  }, item.label), /*#__PURE__*/React.createElement("button", {
    "aria-label": "".concat(removeLabel, " ").concat(item.label),
    className: classes.editChipRemove,
    onClick: () => onRemove(item),
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "close"
  })))));
}

/**
 * Editable bill summary (Figma: "Edit bill details"). Every change is handed straight back up, so the
 * card can recalculate before the next render instead of waiting for a save.
 */
export function BillSummaryEditTable(_ref3) {
  var _ref4, _value$customerName, _value$vendorName, _pickValue, _value$totalAmountDue, _value$totalAmountDue2;
  let customerName = _ref3.customerName,
    onChange = _ref3.onChange,
    value = _ref3.value;
  const classes = useStyles();
  const properties = (value === null || value === void 0 ? void 0 : value.properties) || [];
  const serviceTypes = getServiceTypes(value);
  const customFields = (value === null || value === void 0 ? void 0 : value.customFields) || [];
  const isTotalDerived = properties.some(property => (property.services || []).some(service => (service.lineItems || []).length));
  const glCodeRows = serviceTypes.map(serviceType => {
    const service = properties.flatMap(property => property.services || []).find(item => item.serviceType === serviceType);
    return {
      code: getServiceGlCode(service) || '',
      serviceType
    };
  });
  const setField = patch => onChange(updateBillField(value, patch));
  const setCustomFields = next => setField({
    customFields: next
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.editSectionRow
  }, "Bill summary:"), /*#__PURE__*/React.createElement("div", {
    className: classes.editTable
  }, /*#__PURE__*/React.createElement(EditRow, {
    label: "Customer",
    onDelete: () => setField({
      customerName: ''
    })
  }, /*#__PURE__*/React.createElement(InputBase, {
    className: classes.editInput,
    onChange: event => setField({
      customerName: event.target.value
    }),
    placeholder: "Customer",
    value: (_ref4 = (_value$customerName = value === null || value === void 0 ? void 0 : value.customerName) !== null && _value$customerName !== void 0 ? _value$customerName : customerName) !== null && _ref4 !== void 0 ? _ref4 : ''
  })), /*#__PURE__*/React.createElement(EditRow, {
    label: "Provider",
    onDelete: () => setField({
      vendorName: ''
    })
  }, /*#__PURE__*/React.createElement(InputBase, {
    className: classes.editInput,
    onChange: event => setField({
      vendorName: event.target.value
    }),
    placeholder: "Provider",
    value: (_value$vendorName = value === null || value === void 0 ? void 0 : value.vendorName) !== null && _value$vendorName !== void 0 ? _value$vendorName : ''
  })), /*#__PURE__*/React.createElement(EditRow, {
    deleteLabel: "Remove all utilities",
    label: "Utilities detected",
    onDelete: () => onChange(serviceTypes.reduce((acc, serviceType) => removeServiceType(acc, serviceType), value))
  }, /*#__PURE__*/React.createElement(ChipList, {
    items: serviceTypes.map(serviceType => ({
      key: serviceType,
      label: formatUtilityLabel(serviceType),
      serviceType
    })),
    onRemove: item => onChange(removeServiceType(value, item.serviceType)),
    removeLabel: "Remove"
  })), /*#__PURE__*/React.createElement(EditRow, {
    deleteLabel: "Remove all properties",
    label: "Properties detected",
    onDelete: () => onChange(updateBillField(value, {
      properties: []
    }))
  }, /*#__PURE__*/React.createElement(ChipList, {
    items: properties.map((property, index) => ({
      index,
      key: "".concat(property.address || 'property', "-").concat(index),
      label: property.address || "Property ".concat(index + 1)
    })),
    onRemove: item => onChange(removeProperty(value, item.index)),
    removeLabel: "Remove"
  })), /*#__PURE__*/React.createElement(EditRow, {
    label: "Account #",
    onDelete: () => setField({
      accountNumber: ''
    })
  }, /*#__PURE__*/React.createElement(InputBase, {
    className: classes.editInput,
    onChange: event => setField({
      accountNumber: event.target.value
    }),
    placeholder: "Account number",
    value: (_pickValue = pickValue(value, ['accountNumber', 'invoiceNumber', 'invoiceNo'])) !== null && _pickValue !== void 0 ? _pickValue : ''
  })), /*#__PURE__*/React.createElement(EditRow, {
    label: "Bill date",
    onDelete: () => setField({
      billDate: ''
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.editDateField
  }, /*#__PURE__*/React.createElement(DatePicker, {
    format: "MM/dd/yyyy",
    label: "",
    onChange: date => setField({
      billDate: fromDate(date)
    }),
    value: toDate(pickValue(value, DATE_KEYS.billDate))
  }))), /*#__PURE__*/React.createElement(EditRow, {
    label: "Due date",
    onDelete: () => setField({
      dueDate: ''
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.editDateField
  }, /*#__PURE__*/React.createElement(DatePicker, {
    format: "MM/dd/yyyy",
    label: "",
    onChange: date => setField({
      dueDate: fromDate(date)
    }),
    value: toDate(pickValue(value, DATE_KEYS.dueDate))
  }))), glCodeRows.map(row => /*#__PURE__*/React.createElement(EditRow, {
    key: row.serviceType,
    label: "".concat(formatUtilityLabel(row.serviceType), " GL code"),
    onDelete: () => onChange(setGlCodeForServiceType(value, row.serviceType, ''))
  }, /*#__PURE__*/React.createElement(InputBase, {
    className: classes.editInput,
    onChange: event => onChange(setGlCodeForServiceType(value, row.serviceType, event.target.value)),
    placeholder: "GL code",
    value: row.code
  }))), customFields.map((field, index) => {
    var _field$label, _field$value;
    return /*#__PURE__*/React.createElement("div", {
      className: classes.editRow,
      key: "custom-".concat(index)
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.editCellLabel
    }, /*#__PURE__*/React.createElement(InputBase, {
      className: classes.editInput,
      onChange: event => setCustomFields(customFields.map((item, i) => i === index ? _objectSpread(_objectSpread({}, item), {}, {
        label: event.target.value
      }) : item)),
      placeholder: "Label",
      value: (_field$label = field.label) !== null && _field$label !== void 0 ? _field$label : ''
    })), /*#__PURE__*/React.createElement("div", {
      className: classes.editCellValue
    }, /*#__PURE__*/React.createElement(InputBase, {
      className: classes.editInput,
      onChange: event => setCustomFields(customFields.map((item, i) => i === index ? _objectSpread(_objectSpread({}, item), {}, {
        value: event.target.value
      }) : item)),
      placeholder: "Value",
      value: (_field$value = field.value) !== null && _field$value !== void 0 ? _field$value : ''
    })), /*#__PURE__*/React.createElement("div", {
      className: classes.editCellAction
    }, /*#__PURE__*/React.createElement("button", {
      "aria-label": "Remove this item",
      className: classes.editIconButton,
      onClick: () => setCustomFields(customFields.filter((item, i) => i !== index)),
      type: "button"
    }, /*#__PURE__*/React.createElement(Icon, {
      state: "grey",
      type: "delete"
    }))));
  }), /*#__PURE__*/React.createElement(EditRow, {
    deleteLabel: "Clear total due",
    label: "Total due",
    onDelete: () => onChange(updateTotalDue(value, ''))
  }, isTotalDerived ? /*#__PURE__*/React.createElement("span", {
    className: classes.deductionValue,
    title: "Calculated from the line items below"
  }, formatMoney((_value$totalAmountDue = value === null || value === void 0 ? void 0 : value.totalAmountDue) !== null && _value$totalAmountDue !== void 0 ? _value$totalAmountDue : null, value === null || value === void 0 ? void 0 : value.currency)) : /*#__PURE__*/React.createElement(InputBase, {
    className: classes.editInput,
    onChange: event => onChange(updateTotalDue(value, event.target.value)),
    placeholder: "0.00",
    startAdornment: /*#__PURE__*/React.createElement("span", {
      className: classes.lineItemAdornment
    }, "$"),
    value: (_value$totalAmountDue2 = value === null || value === void 0 ? void 0 : value.totalAmountDue) !== null && _value$totalAmountDue2 !== void 0 ? _value$totalAmountDue2 : ''
  }))), /*#__PURE__*/React.createElement("div", {
    className: classes.editAddRow
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: () => setCustomFields([...customFields, {
      label: '',
      value: ''
    }]),
    size: "small",
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      state: "dark",
      type: "plus"
    }),
    variant: "outlined"
  }, "Add item")));
}